import React, { useState, useEffect, useCallback } from 'react';
import { Link2, ExternalLink, ChevronDown, ChevronUp, ArrowRight, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { hasPI } from '../../../lib/permissions';
import { piApi } from '../../../services/piApi';
import '../PIShell.css';

function holdMonths(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  return Math.max(0, (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth()));
}

function fmt(val) {
  if (val == null) return 'Not set';
  return `$${Number(val).toFixed(1)}M`;
}

function MetricTile({ label, value }) {
  return (
    <div style={{ flex: 1, background: '#f8fafc', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>{value}</div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{label}</div>
    </div>
  );
}

function AddonCard({ link, navigate }) {
  const addon = link.addon;
  if (!addon) return null;
  const hold = holdMonths(link.link_date);
  const holdStr = hold != null ? (hold >= 12 ? `${Math.floor(hold / 12)}y ${hold % 12}m` : `${hold}m`) : '—';

  return (
    <div className="pi-card" style={{ marginBottom: 12 }}>
      <div style={{ padding: '16px 18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              style={{ fontWeight: 600, fontSize: 14, color: '#4f46e5', cursor: 'pointer' }}
              onClick={() => navigate(`/pi/portco/${addon.id}/vcp`)}
            >
              {addon.name}
            </span>
            {addon.sector && (
              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, background: '#f1f5f9', color: '#475569' }}>{addon.sector}</span>
            )}
            <span className={`pi-badge ${link.status === 'active' ? 'pi-badge-green' : 'pi-badge-amber'}`}>
              {link.status === 'active' ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#64748b' }}>
            <span>Closed: {addon.close_date ? new Date(addon.close_date).toLocaleDateString() : '—'}</span>
            <span>Hold: {holdStr}</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          <MetricTile label="Entry Revenue" value={fmt(addon.entry_revenue)} />
          <MetricTile label="Entry EBITDA" value={fmt(addon.entry_ebitda)} />
          <MetricTile label="Entry Multiple" value={addon.entry_multiple != null ? `${Number(addon.entry_multiple).toFixed(1)}x` : 'Not set'} />
        </div>

        <button
          type="button"
          className="pi-btn-ghost"
          style={{ fontSize: 12 }}
          onClick={() => navigate(`/pi/portco/${addon.id}/synergies?portco_link_id=${link.id}`)}
        >
          View Synergies <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
}

export default function PIAddonTab({ portco, currentUser }) {
  const [addonLinks, setAddonLinks] = useState([]);
  const [platformLinks, setPlatformLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showManualLink, setShowManualLink] = useState(false);
  const [allPortcos, setAllPortcos] = useState([]);
  const [linkForm, setLinkForm] = useState({ linked_portco_id: '', role: 'platform', link_date: '' });
  const [linking, setLinking] = useState(false);
  const [manualMode, setManualMode] = useState('link');
  const [createForm, setCreateForm] = useState({ name: '', sector: '', close_date: '', entry_ebitda: '', entry_revenue: '', role: 'platform' });
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const canEdit = hasPI(currentUser, 'pi.addons.edit');

  const load = useCallback(() => {
    piApi.getLinkedPortcos(portco.id)
      .then(r => {
        setAddonLinks(r.data?.addonLinks || []);
        setPlatformLinks(r.data?.platformLinks || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [portco.id]);

  useEffect(() => { load(); }, [load]);

  const loadPortcos = () => {
    if (allPortcos.length) return;
    piApi.getPortcos().then(r => setAllPortcos(r.data || [])).catch(() => {});
  };

  const handleLink = async () => {
    if (!linkForm.linked_portco_id) return;
    setLinking(true);
    await piApi.linkPortco(portco.id, linkForm).catch(() => {});
    setLinking(false);
    setLinkForm({ linked_portco_id: '', role: 'platform', link_date: '' });
    setShowManualLink(false);
    load();
  };

  const handleCreateAndLink = async () => {
    if (!createForm.name.trim()) return;
    setCreating(true);
    await piApi.createAndLinkPortco(portco.id, createForm).catch(() => {});
    setCreating(false);
    setCreateForm({ name: '', sector: '', close_date: '', entry_ebitda: '', entry_revenue: '', role: 'platform' });
    setShowManualLink(false);
    load();
  };

  const linkedIds = new Set([
    ...addonLinks.map(l => l.addon_portco_id),
    ...platformLinks.map(l => l.platform_portco_id),
    portco.id,
  ]);
  const availablePortcos = allPortcos.filter(p => !linkedIds.has(p.id));

  if (loading) {
    return <div className="pi-loading" style={{ padding: 40 }}><div className="pi-spinner" /><span>Loading linked portcos...</span></div>;
  }

  const hasLinks = addonLinks.length > 0 || platformLinks.length > 0;

  return (
    <div style={{ padding: '24px 28px' }}>
      {/* Reverse view: this portco is an addon of a platform company */}
      {platformLinks.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
            Platform Company
          </div>
          {platformLinks.map(link => {
            const platform = link.platform;
            if (!platform) return null;
            return (
              <div key={link.id} className="pi-card" style={{ marginBottom: 8, borderLeft: '3px solid #4f46e5' }}>
                <div style={{ padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Link2 size={16} style={{ color: '#4f46e5' }} />
                    <span
                      style={{ fontWeight: 600, fontSize: 14, color: '#4f46e5', cursor: 'pointer' }}
                      onClick={() => navigate(`/pi/portco/${platform.id}/addons`)}
                    >
                      {platform.name}
                    </span>
                    {platform.sector && (
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, background: '#f1f5f9', color: '#475569' }}>{platform.sector}</span>
                    )}
                  </div>
                  <span style={{ fontSize: 12, color: '#64748b' }}>
                    Linked: {link.link_date ? new Date(link.link_date).toLocaleDateString() : '—'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add-on links: portcos that are add-ons of this company */}
      <div className="pi-card" style={{ marginBottom: 16 }}>
        <div className="pi-card-header">
          <span className="pi-card-title">Linked Portcos</span>
          {addonLinks.length > 0 && (
            <span style={{ fontSize: 12, color: '#64748b' }}>{addonLinks.length} add-on{addonLinks.length !== 1 ? 's' : ''}</span>
          )}
        </div>

        {!hasLinks ? (
          <div className="pi-empty" style={{ padding: 48 }}>
            <Link2 size={28} style={{ color: '#94a3b8', marginBottom: 8 }} />
            <div className="pi-empty-title">No add-ons linked yet</div>
            <div style={{ fontSize: 13, color: '#94a3b8', maxWidth: 420, textAlign: 'center', lineHeight: 1.6, marginBottom: 12 }}>
              Add-on acquisitions are tracked in the DealNexa DD platform.
              When a deal closes and is tagged as an add-on for this company,
              it will appear here automatically.
            </div>
            <a
              href="https://app.deal-nexa.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 12, color: '#4f46e5', display: 'flex', alignItems: 'center', gap: 4 }}
            >
              Go to DD Platform <ExternalLink size={12} />
            </a>
          </div>
        ) : (
          <div style={{ padding: '12px 16px' }}>
            {addonLinks.map(link => (
              <AddonCard key={link.id} link={link} navigate={navigate} />
            ))}
          </div>
        )}
      </div>

      {/* Manual link form */}
      {canEdit && (
        <div style={{ marginTop: 8 }}>
          <button
            type="button"
            className="pi-btn-ghost"
            style={{ fontSize: 12, color: '#94a3b8' }}
            onClick={() => { setShowManualLink(s => !s); loadPortcos(); }}
          >
            {showManualLink ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {' '}Link manually
          </button>

          {showManualLink && (
            <div className="pi-card" style={{ marginTop: 8, padding: 16 }}>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
                Use this only if the DD platform close trigger didn't create the link automatically.
              </div>

              {/* Toggle tabs */}
              <div style={{ display: 'flex', gap: 0, marginBottom: 14, borderBottom: '1px solid #e2e8f0' }}>
                <button
                  type="button"
                  onClick={() => setManualMode('link')}
                  style={{
                    padding: '6px 16px', fontSize: 12, fontWeight: manualMode === 'link' ? 600 : 400,
                    color: manualMode === 'link' ? '#4f46e5' : '#64748b', background: 'none', border: 'none',
                    borderBottom: manualMode === 'link' ? '2px solid #4f46e5' : '2px solid transparent',
                    cursor: 'pointer', marginBottom: -1,
                  }}
                >
                  Link Existing
                </button>
                <button
                  type="button"
                  onClick={() => setManualMode('create')}
                  style={{
                    padding: '6px 16px', fontSize: 12, fontWeight: manualMode === 'create' ? 600 : 400,
                    color: manualMode === 'create' ? '#4f46e5' : '#64748b', background: 'none', border: 'none',
                    borderBottom: manualMode === 'create' ? '2px solid #4f46e5' : '2px solid transparent',
                    cursor: 'pointer', marginBottom: -1,
                  }}
                >
                  <Plus size={11} style={{ marginRight: 3 }} /> Create New
                </button>
              </div>

              {manualMode === 'link' ? (
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 3 }}>Portfolio Company</label>
                    <select
                      value={linkForm.linked_portco_id}
                      onChange={e => setLinkForm(f => ({ ...f, linked_portco_id: e.target.value }))}
                      style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}
                    >
                      <option value="">Select portco...</option>
                      {availablePortcos.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ minWidth: 180 }}>
                    <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 3 }}>Relationship</label>
                    <select
                      value={linkForm.role}
                      onChange={e => setLinkForm(f => ({ ...f, role: e.target.value }))}
                      style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}
                    >
                      <option value="platform">This company is the platform</option>
                      <option value="addon">This company is the add-on</option>
                    </select>
                  </div>
                  <div style={{ minWidth: 140 }}>
                    <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 3 }}>Link Date</label>
                    <input
                      type="date"
                      value={linkForm.link_date}
                      onChange={e => setLinkForm(f => ({ ...f, link_date: e.target.value }))}
                      style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}
                    />
                  </div>
                  <button
                    type="button"
                    className="pi-btn-primary"
                    style={{ fontSize: 12, whiteSpace: 'nowrap' }}
                    onClick={handleLink}
                    disabled={!linkForm.linked_portco_id || linking}
                  >
                    {linking ? 'Linking...' : 'Confirm Link'}
                  </button>
                </div>
              ) : (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                    <div>
                      <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 3 }}>Company Name *</label>
                      <input
                        type="text"
                        value={createForm.name}
                        onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="e.g. Acme Corp"
                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 3 }}>Sector</label>
                      <input
                        type="text"
                        value={createForm.sector}
                        onChange={e => setCreateForm(f => ({ ...f, sector: e.target.value }))}
                        placeholder="e.g. Healthcare IT"
                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 3 }}>Close Date</label>
                      <input
                        type="date"
                        value={createForm.close_date}
                        onChange={e => setCreateForm(f => ({ ...f, close_date: e.target.value }))}
                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 3 }}>Relationship</label>
                      <select
                        value={createForm.role}
                        onChange={e => setCreateForm(f => ({ ...f, role: e.target.value }))}
                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}
                      >
                        <option value="platform">This company is the platform</option>
                        <option value="addon">This company is the add-on</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 3 }}>Entry Revenue ($M)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={createForm.entry_revenue}
                        onChange={e => setCreateForm(f => ({ ...f, entry_revenue: e.target.value }))}
                        placeholder="e.g. 25.0"
                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 3 }}>Entry EBITDA ($M)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={createForm.entry_ebitda}
                        onChange={e => setCreateForm(f => ({ ...f, entry_ebitda: e.target.value }))}
                        placeholder="e.g. 5.0"
                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    className="pi-btn-primary"
                    style={{ fontSize: 12 }}
                    onClick={handleCreateAndLink}
                    disabled={!createForm.name.trim() || creating}
                  >
                    {creating ? 'Creating...' : 'Create & Link Portco'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
